import { useMemo } from 'react'

import { Table, Text } from '@domain-group/suit-theme'

import { digObject, formatDate } from '@domain-group/javascript-utils'

import StatusBadge from '@components/StatusBadge'

import useSelector from '@hooks/useSelector'

import type { PublicationBookingModel } from '@models/types'
import type { EntitiesState } from '@redux/entities'

import formatRelativeDate from '@functions/formatRelativeDate'
import PublicationBookingActions from '../PublicationBookingActions'

const buildColumns = (entities: EntitiesState) => {
  const {
    publications, publicationEditions, publicationProducts, statuses, users,
  } = entities

  return [
    {
      title: '',
      dataKey: 'id',
      render: (id: number) => `# ${id}`,
    },
    {
      title: 'Publication',
      dataKey: 'publication_id',
      render: (publicationId: number) => (
        <Text variant="ellipsis">{publications[publicationId]?.title}</Text>
      ),
    },
    {
      title: 'Product',
      dataKey: 'publication_product_id',
      render: (publicationProductId: number) => (
        <Text variant="ellipsis">{publicationProducts[publicationProductId]?.title}</Text>
      ),
    },
    {
      title: 'Publication Date',
      dataKey: 'publication_edition_id',
      render: (publicationEditionId: number) => {
        const edition = publicationEditions[publicationEditionId] || {}
        return formatDate(edition.publication_date, 'dd/MM/yyyy', 'ccc d LLL')
      },
    },
    {
      title: 'Released',
      dataKey: 'dates',
      render: (dates: object) => {
        const approvalDate = digObject(dates, 'approval.date_time', '')
        return formatRelativeDate(approvalDate)
      },
    },
    {
      title: 'Booking ID',
      dataKey: 'booking_confirmation_id',
      render: (bookingConfirmationId: number) => (bookingConfirmationId ? `# ${bookingConfirmationId}` : ''),
    },
    {
      title: 'Status',
      cellProps: {
        textAlign: 'center',
      },
      dataKey: 'preflight_status_id',
      render: (preflightStatusId: number) => (
        <StatusBadge ghost options={{ defaultStatusKey: 'pending' }} statusId={preflightStatusId} />
      ),
    },
    {
      title: 'Actioned By',
      dataKey: 'preflighted_by_id',
      render: (userId: number, publicationBooking: PublicationBookingModel) => {
        // @ts-expect-error TS2538
        const status = statuses[publicationBooking.preflight_status_id] || {}
        if (status.title !== 'Approved' && status.title !== 'Rejected') return 'N/A'

        const user = users[userId] || {}
        const userText = user.full_name || `User ${userId}`
        const date = digObject(publicationBooking, 'dates.preflighted.date_time', '')

        return `${userText} at ${formatDate(date, 'dd/MM/yyyy HH:mm', 'h:mma \'on\' dd/MM/yyyy')}`
      },
    },
    {
      dataKey: 'id',
      cellProps: {
        textAlign: 'center',
      },
      // @ts-expect-error TS2740
      render: (id: number) => <PublicationBookingActions publicationBooking={{ id }} />,
    },
  ]
}

interface PublicationBookingsTableProps {
  publicationBookings: PublicationBookingModel[],
}

const PublicationBookingsTable = (props: PublicationBookingsTableProps) => {
  const { publicationBookings } = props

  const entities = useSelector(reduxState => reduxState.entities)
  // @ts-expect-error TS2339
  const { _updates: { statuses } } = entities

  const memoColumns = useMemo(() => (
    buildColumns(entities)
  ), [publicationBookings.length, statuses])

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 800, overflowY: 'auto' }}
      // @ts-expect-error TS2322
      columns={memoColumns}
      // @ts-expect-error TS2322
      data={publicationBookings}
      // @ts-expect-error TS2322
      scroll={{ x: 'max-content' }}
      stickyHeader
    />
  )
}

export default PublicationBookingsTable
